import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CalendarDays, Clock, Loader2, Ticket, Bell, Check, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { apiDelete, apiGet } from '../api';
import { formatApiError } from '../lib/apiError';

export const BookingsPage = () => {
  const { api } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, waitlistRes] = await Promise.all([
        apiGet('/api/bookings'),
        apiGet('/api/waitlist')
      ]);
      setBookings(bookingsRes);
      setWaitlistEntries(waitlistRes);
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    // Iter-46c: cancel can take several seconds (Ryft refund + notifications +
    // waitlist promotion). Show a persistent "Processing..." toast and then
    // dismiss it once the request resolves so the user sees progress.
    const loadingToastId = toast.loading('Processing cancellation…');
    try {
      const data = await apiDelete('/api/bookings/{booking_id}', { params: { booking_id: bookingId } });
      toast.dismiss(loadingToastId);
      toast.success(data.refund_details || 'Booking cancelled');
      fetchData();
    } catch (e) {
      toast.dismiss(loadingToastId);
      toast.error(formatApiError(e, 'Failed to cancel booking'));
    }
  };

  const handleLeaveWaitlist = async (waitlistId) => {
    try {
      await apiDelete('/api/waitlist/{waitlist_id}', { params: { waitlist_id: waitlistId } });
      toast.success('Removed from waitlist');
      fetchData();
    } catch (e) {
      toast.error('Failed to leave waitlist');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const now = new Date();
  const confirmedBookings = bookings.filter(b => b.status === 'confirmed');
  const cancelledBookings = bookings
    .filter(b => b.status === 'cancelled')
    .sort((a, b) => new Date(b.class_start_time || 0) - new Date(a.class_start_time || 0));

  // Iter-46g: three-way split of My Bookings.
  //   Upcoming  — confirmed + class_start_time in the future (actionable).
  //               Bug #12 fix: sort ASCENDING (earliest next class first) so
  //               the user's very next booking is at the top instead of the
  //               most-recently-booked one.
  //   Completed — confirmed + class_start_time in the past (attended). Sorted
  //               descending so the most recent attendance is at the top.
  //   Cancelled — status=cancelled (refund status shown), most recent first.
  const upcomingBookings = confirmedBookings
    .filter(b => !b.class_start_time || new Date(b.class_start_time) >= now)
    .sort((a, b) => {
      // Bookings without a start time sink to the bottom so the actionable
      // ones surface first.
      const aTs = a.class_start_time ? new Date(a.class_start_time).getTime() : Infinity;
      const bTs = b.class_start_time ? new Date(b.class_start_time).getTime() : Infinity;
      return aTs - bTs;
    });
  const completedBookings = confirmedBookings
    .filter(b => b.class_start_time && new Date(b.class_start_time) < now)
    .sort((a, b) => new Date(b.class_start_time) - new Date(a.class_start_time));

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">My Bookings</h1>
          <p className="text-muted-foreground">View and manage your class bookings and waitlist</p>
        </div>

        {/* Waitlist Entries */}
        {waitlistEntries.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-600" />
                Waitlist ({waitlistEntries.length})
              </CardTitle>
              <CardDescription>You'll be notified when a spot opens up</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {waitlistEntries.map((entry, i) => (
                  <div 
                    key={entry.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-white border"
                    data-testid={`waitlist-entry-${i}`}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline">Position #{entry.position}</Badge>
                        <span className="font-medium">{entry.class_name}</span>
                        {entry.status === 'promoted' && (
                          <Badge className="bg-emerald-500">Spot Available!</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {entry.class_start_time ? format(new Date(entry.class_start_time), 'EEE, MMM d • h:mm a') : 'Time TBD'}
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleLeaveWaitlist(entry.id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming | Completed | Cancelled tabs */}
        <Tabs defaultValue="upcoming" className="w-full" data-testid="bookings-tabs">
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">
              Upcoming
              {upcomingBookings.length > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5">{upcomingBookings.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">
              Completed
              {completedBookings.length > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5">{completedBookings.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="cancelled" data-testid="tab-cancelled">
              Cancelled
              {cancelledBookings.length > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5">{cancelledBookings.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Classes</CardTitle>
                <CardDescription>
                  {upcomingBookings.length === 0
                    ? 'Nothing scheduled yet'
                    : `${upcomingBookings.length} confirmed booking${upcomingBookings.length === 1 ? '' : 's'}`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {upcomingBookings.length > 0 ? (
                  <div className="space-y-4">
                    {upcomingBookings.map((booking, i) => (
                      <div
                        key={booking.id}
                        className="flex items-center justify-between p-4 rounded-lg border"
                        data-testid={`booking-${i}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge>Confirmed</Badge>
                            <h4 className="font-semibold">{booking.class_name}</h4>
                            {booking.used_class_pass && (
                              <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                                <Ticket className="w-3 h-3 mr-1" />
                                ClassPass
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="w-4 h-4" />
                              {booking.class_start_time ? format(new Date(booking.class_start_time), 'EEE, MMM d, yyyy') : 'Date TBD'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {booking.class_start_time ? format(new Date(booking.class_start_time), 'h:mm a') : 'Time TBD'}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelBooking(booking.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`cancel-booking-${i}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">No upcoming bookings</p>
                    <p className="text-xs text-muted-foreground mt-1">Browse the schedule to book your next class.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="completed" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Completed Classes</CardTitle>
                <CardDescription>
                  {completedBookings.length === 0
                    ? 'Your attendance log will show here once you attend your first class.'
                    : `${completedBookings.length} class${completedBookings.length === 1 ? '' : 'es'} attended`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {completedBookings.length > 0 ? (
                  <div className="space-y-3">
                    {completedBookings.map((booking, i) => (
                      <div
                        key={booking.id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-muted/20"
                        data-testid={`completed-booking-${i}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200">
                              <Check className="w-3 h-3 mr-1" />
                              Attended
                            </Badge>
                            <h4 className="font-semibold">{booking.class_name}</h4>
                            {booking.used_class_pass && (
                              <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                                <Ticket className="w-3 h-3 mr-1" />
                                ClassPass
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(booking.class_start_time), 'EEE, MMM d, yyyy • h:mm a')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Check className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">No completed classes yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cancelled" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Cancelled Bookings</CardTitle>
                <CardDescription>
                  {cancelledBookings.length === 0
                    ? 'No cancellations on record.'
                    : `${cancelledBookings.length} cancelled booking${cancelledBookings.length === 1 ? '' : 's'}`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {cancelledBookings.length > 0 ? (
                  <div className="space-y-3">
                    {cancelledBookings.map((booking, i) => (
                      <div
                        key={booking.id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-muted/50 opacity-75"
                        data-testid={`cancelled-booking-${i}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="secondary">Cancelled</Badge>
                            <h4 className="font-semibold">{booking.class_name}</h4>
                            {booking.cancellation_refunded && (
                              <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                                Refunded
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {booking.class_start_time
                              ? format(new Date(booking.class_start_time), 'EEE, MMM d, yyyy • h:mm a')
                              : 'Time TBD'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <X className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">No cancelled bookings</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};
